import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Input from '../components/UI/Input';
import Button from '../components/UI/Button';
import { Moon, Sun, Bot, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, signup } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isBetaSignup = location.state?.beta;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      if (isSigningUp) {
        await signup(email, password, name);

        // If this is a beta signup, create a customer record with beta tier
        if (isBetaSignup) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase.from('stripe_customers').insert({
              user_id: user.id,
              customer_id: `beta_${user.id}`,
              subscription_tier: 'beta'
            });
          }
        }

        const from = location.state?.from?.pathname || '/app';
        navigate(from, { replace: true });
      } else {
        await login(email, password);
        const from = location.state?.from?.pathname || '/app';
        navigate(from, { replace: true });
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Une erreur est survenue');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col justify-center items-center px-4 bg-gray-50 dark:bg-gray-900">
      <button 
        className="absolute top-4 right-4 p-2 rounded-md bg-white dark:bg-gray-800 shadow-sm"
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
      >
        {theme === 'dark' ? (
          <Sun className="h-5 w-5 text-gray-700 dark:text-gray-300" />
        ) : (
          <Moon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
        )}
      </button>

      <button 
        className="absolute top-4 left-4 p-2 rounded-md bg-white dark:bg-gray-800 shadow-sm"
        onClick={() => navigate('/')}
        aria-label="Retour à l'accueil"
      >
        <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-gray-300" />
      </button>
      
      <div className="max-w-md w-full space-y-8 animate-fade-in">
        <div className="text-center">
          <div className="flex justify-center">
            <img 
              src={theme === 'dark' ? '/logoblanc.png' : '/logo.png'} 
              alt="Invoice AI Logo" 
              className="h-16 w-auto" 
            />
          </div>
        </div>
        
        <div className="mt-8 card space-y-6">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white">
            {isSigningUp ? (isBetaSignup ? 'Inscription Beta' : 'Créer un compte') : 'Connexion à votre compte'}
          </h2>
          
          {error && (
            <div className="bg-error-50 dark:bg-error-900/20 p-3 rounded-md border border-error-200 dark:border-error-800">
              <p className="text-error-600 dark:text-error-400 text-sm">{error}</p>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            {isSigningUp && (
              <Input
                label="Nom"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            )}
            
            <Input
              label="Adresse email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            
            <Input
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isSigningUp ? 'new-password' : 'current-password'}
            />
            
            {!isSigningUp && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember_me"
                    name="remember_me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700 dark:focus:ring-primary-400"
                  />
                  <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Se souvenir de moi
                  </label>
                </div>
                
                <a href="#" className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300">
                  Mot de passe oublié ?
                </a>
              </div>
            )}
            
            <div>
              <Button
                type="submit"
                fullWidth
                isLoading={isLoading}
              >
                {isLoading ? 'Chargement...' : isSigningUp ? (isBetaSignup ? 'S\'inscrire à la Beta' : 'Créer un compte') : 'Se connecter'}
              </Button>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isSigningUp ? (
                  <>
                    Déjà un compte ?{' '}
                    <button
                      type="button"
                      onClick={() => setIsSigningUp(false)}
                      className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      Se connecter
                    </button>
                  </>
                ) : (
                  <>
                    Pas encore de compte ?{' '}
                    <button
                      type="button"
                      onClick={() => setIsSigningUp(true)}
                      className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      S'inscrire
                    </button>
                  </>
                )}
              </p>
            </div>
          </form>
        </div>
      </div>
      
      <div className="mt-8 flex items-center text-sm text-gray-500 dark:text-gray-400">
        <Bot className="h-4 w-4 mr-1" /> 
        Propulsé par l'IA
      </div>
    </div>
  );
};

export default Login;